import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { lookupAPI, lookupTableAPI, staffAPI } from '../services/api';
import ContentHeader from '../components/ContentHeader';
import DeleteIconButton from '../components/DeleteIconButton';
import { formatDisplayDate } from '../utils/dateFormat';
import './LookupTableManager.css';

/** Diagnosis lookup: checkboxes stored as booleans (same UI pattern as Auto eligible). */
const DIAGNOSIS_BOOLEAN_FIELDS = new Set(['auto_eligible', 'low_incident_disability', 'dates_required_default']);

function LookupTableManager() {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const returnToPath = location.state?.returnToPath || null;
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [referralLevel1Options, setReferralLevel1Options] = useState([]);
  const [staffSaveStatus, setStaffSaveStatus] = useState(null); // 'saving' | 'saved' | 'error'
  const staffLastSavedRef = useRef(null); // For staff edit: skip save if formData matches initial load
  const formCardRef = useRef(null);

  // Map table IDs to API methods and display fields
  const tableConfig = {
    'counties': {
      apiMethod: () => lookupAPI.getCounties(),
      fields: ['county', 'code', 'cs_code', 'is_active'],
      displayFields: { county: 'County Name', code: 'Code', cs_code: 'CS Code', is_active: 'Status' },
      tableFields: ['county', 'code', 'cs_code', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'cities': {
      apiMethod: () => lookupAPI.getCities(),
      fields: ['city', 'is_active'],
      displayFields: { city: 'City Name', is_active: 'Status' },
      tableFields: ['city', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'languages': {
      apiMethod: () => lookupAPI.getLanguages(),
      fields: ['code', 'description', 'is_active'],
      displayFields: { code: 'Code', description: 'Description', is_active: 'Status' },
      tableFields: ['code', 'description', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'races': {
      apiMethod: () => lookupAPI.getRaces(),
      fields: ['code', 'description', 'is_active'],
      displayFields: { code: 'Code', description: 'Description', is_active: 'Status' },
      tableFields: ['code', 'description', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'termination-reasons': {
      apiMethod: () => lookupAPI.getTerminationReasons(),
      fields: ['code', 'description', 'is_active'],
      displayFields: { code: 'Code', description: 'Description', is_active: 'Status' },
      tableFields: ['code', 'description', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'living-arrangements': {
      apiMethod: null,
      fields: ['code', 'description', 'is_active'],
      displayFields: { code: 'Code', description: 'Description', is_active: 'Status' },
      tableFields: ['code', 'description', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'insurance-companies': {
      apiMethod: null,
      fields: ['name', 'is_active'],
      displayFields: { name: 'Name', is_active: 'Status' },
      tableFields: ['name', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'insurance-types': {
      apiMethod: null,
      fields: ['code', 'description', 'is_active'],
      displayFields: { code: 'Code', description: 'Description', is_active: 'Status' },
      tableFields: ['code', 'description', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'referral-level-1': {
      apiMethod: null,
      fields: ['code', 'description', 'sort_order', 'is_active'],
      displayFields: { code: 'Code', description: 'Description', sort_order: 'Sort Order', is_active: 'Status' },
      tableFields: ['code', 'description', 'sort_order', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'referral-level-2': {
      apiMethod: null,
      fields: ['referral_level_1_id', 'description', 'is_active'],
      displayFields: { referral_level_1_id: 'Referral Source Level 1', description: 'Level 2 description', is_active: 'Status' },
      tableFields: ['referral_level_1_id', 'description', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'diagnosis-autoeligible': {
      apiMethod: null,
      pageTitle: 'Diagnosis',
      fields: ['description', 'auto_eligible', 'low_incident_disability', 'is_active'],
      displayFields: {
        description: 'Description',
        auto_eligible: 'Auto eligible',
        low_incident_disability: 'Low incident disability',
        is_active: 'Status',
      },
      tableFields: ['description', 'auto_eligible', 'low_incident_disability', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true,
    },
    'provider-types': {
      apiMethod: () => lookupAPI.getProviderTypes(),
      fields: ['code', 'name', 'service_delay_code', 'is_active'],
      displayFields: {
        code: 'Code',
        name: 'Name',
        service_delay_code: 'Service delay code',
        is_active: 'Status',
      },
      tableFields: ['code', 'name', 'service_delay_code', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'credential-codes': {
      apiMethod: null,
      fields: ['code', 'name', 'is_active'],
      displayFields: { code: 'Code', name: 'Display Name', is_active: 'Status' },
      tableFields: ['code', 'name', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'service-areas': {
      apiMethod: null,
      fields: ['name', 'code', 'is_active'],
      displayFields: { name: 'Name', code: 'Code', is_active: 'Status' },
      tableFields: ['name', 'code', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'frequencies': {
      apiMethod: null,
      fields: ['code', 'description', 'list_order', 'monthly_amount', 'is_active'],
      displayFields: {
        code: 'Code',
        description: 'Description',
        list_order: 'List Order',
        monthly_amount: 'Monthly Amount',
        is_active: 'Active'
      },
      tableFields: ['code', 'description', 'list_order', 'monthly_amount', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'delay-reasons': {
      apiMethod: null,
      fields: ['code', 'description', 'is_active'],
      displayFields: { code: 'Code', description: 'Description', is_active: 'Status' },
      tableFields: ['code', 'description', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'tracking-statuses': {
      apiMethod: null,
      fields: ['name', 'is_active'],
      displayFields: { name: 'Name', is_active: 'Status' },
      tableFields: ['name', 'is_active'],
      keyField: 'id',
      hasActiveToggle: true
    },
    'work-types': {
      apiMethod: null,
      fields: ['name', 'sort_order'],
      displayFields: { name: 'Name', sort_order: 'Sort Order' },
      tableFields: ['name', 'sort_order'],
      keyField: 'id'
    },
    'task-types': {
      apiMethod: null,
      fields: ['name', 'sort_order'],
      displayFields: { name: 'Name', sort_order: 'Sort Order' },
      tableFields: ['name', 'sort_order'],
      keyField: 'id'
    },
    'at-packet-insurance-types': {
      apiMethod: null,
      pageTitle: 'Insurance types (AT packets)',
      fields: ['code', 'display_name', 'sort_order'],
      displayFields: { code: 'Code', display_name: 'Display name', sort_order: 'Sort order' },
      tableFields: ['code', 'display_name', 'sort_order'],
      keyField: 'id',
    },
    'at-packet-statuses': {
      apiMethod: null,
      pageTitle: 'AT packet statuses',
      adminNote:
        'Packets store a status by ID. Automation (date fields → status, provider emails, inbox) matches the built-in Code values (e.g. Approved, SubmittedATC). Change Display name for wording users see; avoid changing Code on existing built-in rows—the server blocks renames of those codes. New statuses: pick a new unique Code once; you can edit Display name anytime.',
      fields: ['code', 'display_name', 'sort_order'],
      displayFields: { code: 'Code', display_name: 'Display name', sort_order: 'Sort order' },
      tableFields: ['code', 'display_name', 'sort_order'],
      keyField: 'id',
    },
    'at-packet-dscc-statuses': {
      apiMethod: null,
      pageTitle: 'DSCC statuses (Client)',
      fields: ['code', 'display_name', 'sort_order'],
      displayFields: { code: 'Code', display_name: 'Display name', sort_order: 'Sort order' },
      tableFields: ['code', 'display_name', 'sort_order'],
      keyField: 'id',
    },
    'at-packet-item-types': {
      apiMethod: null,
      pageTitle: 'Item types (AT packets)',
      adminNote: 'Item types appear in the Type dropdown when adding items to an AT packet. "Dates required" controls whether Request/Received date fields are required for that item type.',
      fields: ['code', 'display_name', 'dates_required_default', 'sort_order'],
      displayFields: { code: 'Code', display_name: 'Display name', dates_required_default: 'Dates required', sort_order: 'Sort order' },
      tableFields: ['code', 'display_name', 'dates_required_default', 'sort_order'],
      keyField: 'id',
    },
    'at-packet-item-names': {
      apiMethod: null,
      pageTitle: 'Item names / AT products (AT packets)',
      adminNote: 'These are the AT product options shown in the Name dropdown when adding an AT item to a packet.',
      fields: ['code', 'display_name', 'sort_order'],
      displayFields: { code: 'Code', display_name: 'Display name', sort_order: 'Sort order' },
      tableFields: ['code', 'display_name', 'sort_order'],
      keyField: 'id',
    },
    'staff': {
      apiMethod: () => staffAPI.getAll({}),
      fields: ['first_name', 'last_name', 'email', 'phone', 'fax', 'team', 'credential_status', 'hire_date', 'termination_date', 'is_active'],
      displayFields: { 
        first_name: 'First Name', 
        last_name: 'Last Name', 
        email: 'Email', 
        phone: 'Phone',
        fax: 'Fax',
        team: 'Team',
        credential_status: 'Credential Status',
        hire_date: 'Hire Date',
        termination_date: 'Termination Date',
        is_active: 'Active'
      },
      // Fields to show in the table (subset of all fields)
      tableFields: ['first_name', 'last_name', 'email', 'is_active'],
      keyField: 'id',
      allowDelete: false // Staff should not be deleted, only made inactive
    }
  };

  const config = tableConfig[tableId];
  const supportsHeaderSort = tableId === 'work-types' || tableId === 'task-types'
    || tableId === 'at-packet-insurance-types' || tableId === 'at-packet-dscc-statuses'
    || tableId === 'at-packet-statuses' || tableId === 'at-packet-item-types'
    || tableId === 'at-packet-item-names';

  // Lookups with "Active" column + header checkbox, plain Active/Inactive text, no Actions column
  const activeFilterInHeaderTables = [
    'counties', 'cities', 'languages', 'races',
    'living-arrangements', 'insurance-companies', 'insurance-types',
    'referral-level-1', 'referral-level-2', 'diagnosis-autoeligible', 'termination-reasons',
    'provider-types', 'credential-codes', 'service-areas', 'frequencies', 'delay-reasons'
  ];

  useEffect(() => {
    if (config) {
      loadItems();
    }
  }, [tableId, includeInactive]);

  useEffect(() => {
    if (supportsHeaderSort) {
      setSortField('sort_order');
      setSortDir('asc');
    } else {
      setSortField(null);
      setSortDir('asc');
    }
  }, [supportsHeaderSort]);

  // Load Referral Source Level 1 options when managing referral-level-2
  useEffect(() => {
    if (tableId === 'referral-level-2') {
      lookupTableAPI.getAll('referral-level-1', { includeInactive: true })
        .then((res) => setReferralLevel1Options(res.data?.data || []))
        .catch(() => setReferralLevel1Options([]));
    } else {
      setReferralLevel1Options([]);
    }
  }, [tableId]);

  // Staff edit: auto-save when formData changes (debounced)
  useEffect(() => {
    if (tableId !== 'staff' || !editingId) return;
    const current = JSON.stringify(formData);
    if (current === staffLastSavedRef.current) return;

    const t = setTimeout(async () => {
      setStaffSaveStatus('saving');
      try {
        await staffAPI.update(editingId, formData);
        staffLastSavedRef.current = current;
        await loadItems({ skipLoading: true });
        setStaffSaveStatus('saved');
        setTimeout(() => setStaffSaveStatus(null), 2000);
      } catch (err) {
        setStaffSaveStatus('error');
        setTimeout(() => setStaffSaveStatus(null), 3000);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [formData, editingId, tableId]);

  const loadItems = async (options = {}) => {
    const skipLoading = options.skipLoading === true;
    try {
      if (!skipLoading) setLoading(true);
      if (tableId === 'staff') {
        const response = await staffAPI.getAll(includeInactive ? {} : { active: 'true' });
        setItems(response.data.data || []);
      } else {
        const response = await lookupTableAPI.getAll(tableId, { includeInactive });
        setItems(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading items:', error);
      if (!skipLoading) alert('Failed to load items');
    } finally {
      if (!skipLoading) setLoading(false);
    }
  };

  const handleEdit = (item) => {
    if (!config) return;
    const id = item[config.keyField];
    if (id === undefined || id === null) return;
    setEditingId(id);
    // Create a copy of the item with all form fields initialized
    const editData = {};
    config.fields.forEach(field => {
      if (field === 'is_active') {
        editData[field] = item[field] !== undefined ? item[field] : false;
      } else if (field === 'service_delay_code') {
        editData[field] = item[field] !== undefined && item[field] !== null ? String(item[field]) : '0';
      } else if (field === 'sort_order') {
        editData[field] = item[field] !== undefined && item[field] !== null ? String(item[field]) : '0';
      } else if (DIAGNOSIS_BOOLEAN_FIELDS.has(field)) {
        editData[field] = item[field] === true || item[field] === 1;
      } else if (field === 'credential_status') {
        editData[field] = item[field] || 'FULL';
      } else if (field === 'hire_date' || field === 'termination_date') {
        // Format date for date input (YYYY-MM-DD)
        editData[field] = item[field] ? item[field].split('T')[0] : '';
      } else {
        editData[field] = item[field] !== undefined && item[field] !== null ? item[field] : '';
      }
    });
    setFormData(editData);
    setShowAddForm(true);
    if (tableId === 'staff') {
      staffLastSavedRef.current = JSON.stringify(editData);
      setStaffSaveStatus(null);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setShowAddForm(false);
    // Reset form data based on config fields
    const resetData = {};
    if (config) {
      config.fields.forEach(field => {
        if (field === 'is_active') {
          resetData[field] = false;
        } else if (DIAGNOSIS_BOOLEAN_FIELDS.has(field)) {
          resetData[field] = false;
        } else if (field === 'credential_status') {
          resetData[field] = 'FULL';
        } else if (field === 'sort_order') {
          resetData[field] = '0';
        } else {
          resetData[field] = '';
        }
      });
    }
    setFormData(resetData);
  };

  const handleSave = async () => {
    try {
      if (tableId === 'staff') {
        // Staff uses different API
        if (editingId) {
          await staffAPI.update(editingId, formData);
        } else {
          await staffAPI.create(formData);
        }
      } else {
        // Other lookup tables use lookup table API
        if (editingId) {
          await lookupTableAPI.update(tableId, editingId, formData);
        } else {
          await lookupTableAPI.create(tableId, formData);
        }
      }
      await loadItems();
      handleCancel();
    } catch (error) {
      console.error('Error saving item:', error);
      const errorMsg = error.response?.data?.error || 'Failed to save item';
      alert(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }
    try {
      if (tableId === 'staff') {
        await staffAPI.delete(id);
      } else {
        await lookupTableAPI.delete(tableId, id);
      }
      await loadItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      const errorMsg = error.response?.data?.error || 'Failed to delete item';
      alert(errorMsg);
    }
  };

  const handleToggleActive = async (item) => {
    const id = item[config.keyField];
    const newActive = !(item.is_active !== false && item.is_active !== undefined);
    try {
      await lookupTableAPI.update(tableId, id, { is_active: newActive });
      await loadItems();
    } catch (error) {
      console.error('Error updating status:', error);
      const errorMsg = error.response?.data?.error || 'Failed to update status';
      alert(errorMsg);
    }
  };

  // When edit form opens (e.g. staff row click), scroll form into view so the user sees it
  useEffect(() => {
    if (editingId && formCardRef.current) {
      const id = requestAnimationFrame(() => {
        formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      return () => cancelAnimationFrame(id);
    }
  }, [editingId]);

  if (!config) {
    return (
      <div className="lookup-manager">
        <div className="error-message">Invalid lookup table: {tableId}</div>
      </div>
    );
  }

  const tableName = config.pageTitle || tableId.split('-').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');

  const handleHeaderSort = (field) => {
    if (!supportsHeaderSort) return;
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Staff: sort by last name then first name ascending
  const displayItems = tableId === 'staff'
    ? [...items].sort((a, b) => {
        const la = (a.last_name || '').toLowerCase();
        const lb = (b.last_name || '').toLowerCase();
        if (la !== lb) return la.localeCompare(lb);
        return (a.first_name || '').toLowerCase().localeCompare((b.first_name || '').toLowerCase());
      })
    : supportsHeaderSort
      ? [...items].sort((a, b) => {
          const aValue = a?.[sortField];
          const bValue = b?.[sortField];
          let result = 0;
          if (sortField === 'sort_order') {
            const an = Number.isFinite(Number(aValue)) ? Number(aValue) : Number.MAX_SAFE_INTEGER;
            const bn = Number.isFinite(Number(bValue)) ? Number(bValue) : Number.MAX_SAFE_INTEGER;
            result = an - bn;
          } else {
            result = String(aValue ?? '').localeCompare(String(bValue ?? ''), undefined, { sensitivity: 'base' });
          }
          return sortDir === 'asc' ? result : -result;
        })
      : items;

  const handleAddNew = () => {
    setShowAddForm(true);
    setEditingId(null);
    const initialData = {};
    config.fields.forEach(field => {
      if (field === 'is_active') {
        initialData[field] = config.hasActiveToggle ? true : false;
      } else if (DIAGNOSIS_BOOLEAN_FIELDS.has(field)) {
        initialData[field] = false;
      } else if (field === 'credential_status') {
        initialData[field] = 'FULL';
      } else if (field === 'service_delay_code' && tableId === 'provider-types') {
        initialData[field] = '0';
      } else if (field === 'sort_order') {
        initialData[field] = '0';
      } else {
        initialData[field] = '';
      }
    });
    setFormData(initialData);
  };

  return (
    <div className="lookup-manager">
      <ContentHeader
        title={tableName}
        actions={
          <div className="lookup-manager-header-actions">
            {returnToPath ? (
              <button type="button" className="btn-return-form" onClick={() => navigate(returnToPath)}>
                Return to referral
              </button>
            ) : null}
            <button type="button" className="btn-add" onClick={handleAddNew}>
              + Add New
            </button>
          </div>
        }
      />

      {config.adminNote && (
        <p className="lookup-manager-admin-note" role="note">
          {config.adminNote}
        </p>
      )}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
          {(showAddForm || editingId) && (
            <div className="lookup-form-card" ref={formCardRef}>
              <h3>{editingId ? 'Edit' : 'Add New'} {tableName}</h3>
              <div className="form-grid">
                {config.fields.map(field => {
                  // Staff is_active is rendered with termination_date above
                  if (field === 'is_active' && tableId === 'staff') return null;
                  // Special handling for boolean fields
                  if (field === 'is_active') {
                    return (
                      <div key={field} className="form-group form-group-checkbox-inline">
                        <label className="checkbox-label-inline">
                          <input
                            type="checkbox"
                            checked={formData[field] || false}
                            onChange={(e) => setFormData({ ...formData, [field]: e.target.checked })}
                          />
                          <span>{config.displayFields[field]}</span>
                        </label>
                      </div>
                    );
                  }
                  if (DIAGNOSIS_BOOLEAN_FIELDS.has(field)) {
                    return (
                      <div key={field} className="form-group form-group-checkbox-inline">
                        <label className="checkbox-label-inline">
                          <input
                            type="checkbox"
                            checked={formData[field] === true || formData[field] === 1}
                            onChange={(e) => setFormData({ ...formData, [field]: e.target.checked })}
                          />
                          <span>{config.displayFields[field]}</span>
                        </label>
                      </div>
                    );
                  }
                  
                  // Special handling for date fields
                  if (field === 'hire_date' || field === 'termination_date') {
                    // Staff: put termination_date and Active checkbox on same row
                    if (tableId === 'staff' && field === 'termination_date') {
                      return (
                        <React.Fragment key="termination_date-active-row">
                          <div key="termination_date" className="form-group">
                            <label>{config.displayFields.termination_date}</label>
                            <input
                              type="date"
                              value={formData.termination_date || ''}
                              onChange={(e) => setFormData({ ...formData, termination_date: e.target.value })}
                            />
                          </div>
                          <div key="is_active" className="form-group form-group-checkbox-inline">
                            <label className="checkbox-label-inline">
                              <input
                                type="checkbox"
                                checked={formData.is_active || false}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                              />
                              <span>{config.displayFields.is_active}</span>
                            </label>
                          </div>
                        </React.Fragment>
                      );
                    }
                    return (
                      <div key={field} className="form-group">
                        <label>{config.displayFields[field]}</label>
                        <input
                          type="date"
                          value={formData[field] || ''}
                          onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                        />
                      </div>
                    );
                  }
                  
                  // Special handling for credential_status dropdown
                  if (field === 'credential_status') {
                    return (
                      <div key={field} className="form-group">
                        <label>{config.displayFields[field]}</label>
                        <select
                          value={formData[field] || 'FULL'}
                          onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                        >
                          <option value="FULL">Full</option>
                          <option value="TEMP">Temporary</option>
                          <option value="NA">N/A</option>
                        </select>
                      </div>
                    );
                  }

                  // Referral-level-2: referral_level_1_id as dropdown of Referral Source Level 1
                  if (tableId === 'referral-level-2' && field === 'referral_level_1_id') {
                    return (
                      <div key={field} className="form-group">
                        <label>{config.displayFields[field]}</label>
                        <select
                          value={formData[field] != null ? String(formData[field]) : ''}
                          onChange={(e) => setFormData({ ...formData, [field]: e.target.value ? parseInt(e.target.value, 10) : '' })}
                        >
                          <option value="">Select Referral Source Level 1...</option>
                          {referralLevel1Options.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.description}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  if (tableId === 'provider-types' && field === 'service_delay_code') {
                    return (
                      <div key={field} className="form-group">
                        <label>{config.displayFields[field]}</label>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          inputMode="numeric"
                          value={formData[field] !== undefined && formData[field] !== null && formData[field] !== '' ? String(formData[field]) : ''}
                          onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                          placeholder="e.g. 7 for OT"
                        />
                      </div>
                    );
                  }

                  if (field === 'sort_order') {
                    return (
                      <div key={field} className="form-group">
                        <label>{config.displayFields[field]}</label>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          inputMode="numeric"
                          value={formData[field] !== undefined && formData[field] !== null && formData[field] !== '' ? String(formData[field]) : '0'}
                          onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                          placeholder="0"
                        />
                      </div>
                    );
                  }

                  return (
                    <div key={field} className="form-group">
                      <label>{config.displayFields[field]}</label>
                      <input
                        type="text"
                        value={formData[field] || ''}
                        onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                        placeholder={`Enter ${config.displayFields[field]}`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="form-actions">
                {tableId === 'staff' && editingId ? (
                  <>
                    {staffSaveStatus === 'saving' && <span className="form-status">Saving…</span>}
                    {staffSaveStatus === 'saved' && <span className="form-status form-status-saved">Saved</span>}
                    {staffSaveStatus === 'error' && <span className="form-status form-status-error">Failed to save</span>}
                    <button type="button" className="btn-cancel" onClick={handleCancel}>Cancel</button>
                    <button type="button" className="btn-save" onClick={handleSave}>Update</button>
                  </>
                ) : tableId === 'staff' ? (
                  <>
                    <button type="button" className="btn-cancel" onClick={handleCancel}>Cancel</button>
                    <button type="button" className="btn-save" onClick={handleSave}>Create</button>
                  </>
                ) : (
                  <>
                    <button type="button" className="btn-cancel" onClick={handleCancel}>Cancel</button>
                    <button type="button" className="btn-save" onClick={handleSave}>{editingId ? 'Update' : 'Create'}</button>
                  </>
                )}
              </div>
            </div>
          )}

          {config.hasActiveToggle && tableId !== 'staff' && !activeFilterInHeaderTables.includes(tableId) && (
            <div className="lookup-include-inactive">
              <label>
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
                {' '}Include inactive
              </label>
            </div>
          )}

          <div className="lookup-table-container">
            <table className="lookup-table">
              <thead>
                <tr>
                  {tableId === 'staff' ? (
                    <>
                      <th>Staff name</th>
                      <th>Email</th>
                      <th className="lookup-staff-active-header">
                        Active
                        <label className="lookup-staff-active-only-checkbox">
                          <input
                            type="checkbox"
                            checked={!includeInactive}
                            onChange={(e) => setIncludeInactive(!e.target.checked)}
                            aria-label="Show active staff only"
                            title="Uncheck to show inactive staff"
                          />
                        </label>
                      </th>
                    </>
                  ) : activeFilterInHeaderTables.includes(tableId) ? (
                    <>
                      {(config.tableFields || config.fields).map(field => (
                        field === 'is_active' ? (
                          <th key={field} className="lookup-staff-active-header">
                            Active
                            <label className="lookup-staff-active-only-checkbox">
                              <input
                                type="checkbox"
                                checked={!includeInactive}
                                onChange={(e) => setIncludeInactive(!e.target.checked)}
                                aria-label="Show active only (uncheck to show all)"
                                title="Checked: active only. Uncheck to show all."
                              />
                            </label>
                          </th>
                        ) : (
                          <th key={field}>{config.displayFields[field]}</th>
                        )
                      ))}
                    </>
                  ) : (
                    <>
                      {(config.tableFields || config.fields).map(field => (
                        <th
                          key={field}
                          className={supportsHeaderSort ? 'lookup-sortable-header' : ''}
                          onClick={() => handleHeaderSort(field)}
                          title={supportsHeaderSort ? 'Sort' : undefined}
                        >
                          {config.displayFields[field]}
                          {supportsHeaderSort && (
                            <span className="lookup-sort-indicator">
                              {sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
                            </span>
                          )}
                        </th>
                      ))}
                      <th>Actions</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {displayItems.length === 0 ? (
                  <tr>
                    <td colSpan={tableId === 'staff' ? 3 : activeFilterInHeaderTables.includes(tableId) ? (config.tableFields || config.fields).length : (config.tableFields || config.fields).length + 1} className="empty-message">
                      No items found
                    </td>
                  </tr>
                ) : tableId === 'staff' ? (
                  displayItems.map((item) => (
                    <tr
                      key={String(item[config.keyField])}
                      className="clickable-row"
                      onClick={() => handleEdit(item)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEdit(item); } }}
                    >
                      <td>{[item.last_name, item.first_name].filter(Boolean).join(', ') || '—'}</td>
                      <td>{item.email || '—'}</td>
                      <td>{item.is_active !== false && item.is_active !== undefined ? 'Active' : 'Inactive'}</td>
                    </tr>
                  ))
                ) : (
                  displayItems.map((item) => (
                    <tr
                      key={String(item[config.keyField])}
                      className={`${config.hasActiveToggle && !activeFilterInHeaderTables.includes(tableId) && (item.is_active === false) ? 'row-inactive' : ''} clickable-row`}
                      onClick={() => handleEdit(item)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEdit(item); } }}
                    >
                      {(config.tableFields || config.fields).map(field => {
                        if (field === 'is_active') {
                          const isActiveFilterTable = activeFilterInHeaderTables.includes(tableId);
                          return (
                            <td key={field}>
                              {isActiveFilterTable ? (
                                item[field] !== false && item[field] !== undefined ? 'Active' : 'Inactive'
                              ) : (
                                item[field] !== false && item[field] !== undefined ? (
                                  <span className="badge-active">Active</span>
                                ) : (
                                  <span className="badge-inactive">Inactive</span>
                                )
                              )}
                            </td>
                          );
                        }
                        if (DIAGNOSIS_BOOLEAN_FIELDS.has(field)) {
                          const checked = item[field] === true || item[field] === 1;
                          return (
                            <td key={field}>
                              <span className="lookup-table-boolean-cell" aria-hidden="true" title={checked ? 'Yes' : 'No'}>
                                <span className={`lookup-table-boolean-box ${checked ? 'checked' : ''}`}>
                                  {checked ? '✓' : ''}
                                </span>
                              </span>
                            </td>
                          );
                        }
                        if (field === 'hire_date' || field === 'termination_date') {
                          return (
                            <td key={field}>
                              {item[field] ? formatDisplayDate(item[field]) : '—'}
                            </td>
                          );
                        }
                        // Referral-level-2: show Level 1 description instead of id
                        if (tableId === 'referral-level-2' && field === 'referral_level_1_id') {
                          const level1 = referralLevel1Options.find((o) => Number(o.id) === Number(item.referral_level_1_id));
                          return <td key={field}>{level1 ? level1.description : (item.referral_level_1_id ?? '—')}</td>;
                        }
                        const raw = item[field];
                        const show =
                          raw === 0 || raw === false
                            ? String(raw)
                            : raw == null || raw === ''
                              ? '—'
                              : raw;
                        return <td key={field}>{show}</td>;
                      })}
                      {!activeFilterInHeaderTables.includes(tableId) && (
                        <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                          {config.hasActiveToggle ? (
                            <label className="toggle-active-label">
                              <input
                                type="checkbox"
                                className="toggle-active-input"
                                checked={item.is_active !== false && item.is_active !== undefined}
                                onChange={() => handleToggleActive(item)}
                              />
                              <span className="toggle-active-slider" />
                            </label>
                          ) : config.allowDelete !== false ? (
                            <DeleteIconButton
                              className="btn-delete"
                              onClick={() => handleDelete(item[config.keyField])}
                            />
                          ) : null}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default LookupTableManager;
